// Enums from schema.yaml
export type ActionTemplatePurpose = 'assets' | 'crisis' | 'risks';
export type ActionTemplateArea = string | number;
export type ActionTemplateStatus = 'active' | 'inactive';
export type ActionTemplateCriticality = 'high' | 'medium' | 'low';
export type ActionTemplateOwnerType = 'pocketsafe' | 'organization';

export const ACTION_TEMPLATE_OWNER_TYPE_TO_MESSAGE_KEY: Record<ActionTemplateOwnerType, 'pocketsafe' | 'organization'> = {
  pocketsafe: 'pocketsafe',
  organization: 'organization',
};

export type ActionTemplateHistoryActionType = 'created' | 'updated' | 'copied' | 'activated' | 'deactivated' | 'soft_deleted' | 'restored';
export type ActionTemplateStepStatus = 'pending' | 'in_progress' | 'completed';

// Shared Generics
export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

// Step types
export type ActionTemplateStepDTO = {
  id: number;
  title: string;
  short_description: string;
  criticality: ActionTemplateCriticality;
  criticality_display: string;
  tags: Record<string, unknown>;
  reference: string;
  reported_to: string;
  relevant_for: string;
  status: ActionTemplateStepStatus;
  status_display: string;
  order: number;
  mandatory?: boolean;
};

export type ActionTemplateStepCreateDTO = {
  title: string;
  short_description: string;
  criticality?: ActionTemplateCriticality;
  tags?: Record<string, unknown>;
  reference?: string;
  reported_to?: string;
  relevant_for?: string;
  status?: ActionTemplateStepStatus;
  order?: number;
  mandatory?: boolean;
};

// Shared Types
export type ActionTemplateAreaDataDTO = {
  id: number;
  name: string;
  name_translation_key: string;
  code: string;
  display_name: string;
  display_name_translation_key: string;
  slug: string;
  is_active: boolean;
};

export type ActionTemplateSubAreaDataDTO = {
  id: number;
  name: string;
  name_translation_key: string;
  area: ActionTemplateAreaDataDTO;
  slug: string;
  is_active: boolean;
};

export type ActionTemplateAreaData = {
  id: number;
  name: string;
  nameTranslationKey: string;
  code: string;
  displayName: string;
  displayNameTranslationKey: string;
  slug: string;
  isActive: boolean;
};

export type ActionTemplateSubAreaData = {
  id: number;
  name: string;
  nameTranslationKey: string;
  slug: string;
  isActive: boolean;
};

// List DTO (for list endpoint)
export type ActionTemplateListDTO = {
  id: number;
  name: string;
  short_description: string;
  purpose: ActionTemplatePurpose;
  purpose_display: string;
  area: ActionTemplateArea;
  area_display: string;
  area_data?: ActionTemplateAreaDataDTO;
  sub_area: string | number | null;
  sub_area_data?: ActionTemplateSubAreaDataDTO;
  status: ActionTemplateStatus;
  status_display: string;
  criticality: ActionTemplateCriticality;
  criticality_display: string;
  owner_type: ActionTemplateOwnerType;
  owner_type_display: string;
  action_status: string;
  created_at: string;
  created_by: ActionTemplateCreatedByDTO;
  updated_at: string;
  tags: Record<string, unknown>;
  steps: ActionTemplateStepDTO[];
  reference?: string;
  reported_to?: string;
  asset_categories?: AssetCategoryDTO[];
  validity_period_months?: number | null;
  is_locked?: boolean;
  locked_by_user?: ActionTemplateCreatedByDTO;
  lock_info?: string;
  is_fresh_change?: boolean;
  has_impending_changes?: boolean;
  last_published_at?: string;
  instances_count_all_orgs?: string;
  instances_count_org?: string;
};

// Detail DTO (for detail endpoint) - owner_organization can be string or object from API
export type ActionTemplateOwnerOrganizationDTO = {
  id: number;
  name: string;
};

export type ActionTemplateDetailDTO = ActionTemplateListDTO & {
  steps: ActionTemplateStepDTO[];
  validity_period_months: number | null;
  created_by: ActionTemplateCreatedByDTO | null;
  owner_organization: string | ActionTemplateOwnerOrganizationDTO;
  assets: string;
  asset_categories: AssetCategoryDTO[];
  is_deleted: boolean;
  reference?: string;
  reported_to?: string;
  locked_by_user?: ActionTemplateCreatedByDTO;
  is_locked?: boolean;
  lock_info?: string;
  is_fresh_change?: boolean;
  has_impending_changes?: boolean;
  last_published_at?: string;
};

// Create DTO
export type ActionTemplateCreateDTO = {
  name: string;
  short_description: string;
  purpose: ActionTemplatePurpose;
  area: ActionTemplateArea;
  sub_area?: string | number;
  status?: ActionTemplateStatus;
  owner_type?: ActionTemplateOwnerType;
  owner_organization_id?: number | null;
  tags?: string[];
  validity_period_months?: number | null;
  criticality?: ActionTemplateCriticality;
  steps?: ActionTemplateStepCreateDTO[];
  asset_ids?: number[];
  asset_category_ids?: number[];
  reference?: string;
  reported_to?: string;
};

// Update DTO
export type ActionTemplateUpdateDTO = Partial<Omit<ActionTemplateCreateDTO, 'asset_category_ids'>> & {
  is_deleted?: boolean;
};

// History DTO
export type ActionTemplateHistoryDTO = {
  id: number;
  action_type: ActionTemplateHistoryActionType;
  action_type_display: string;
  template_name_snapshot: string;
  performed_by: string;
  note: string;
  created_at: string;
};

// Response types
export type ActionTemplateListResponse = PaginatedResponse<ActionTemplateListDTO>;

export type ActionTemplateHistoryListResponse = PaginatedResponse<ActionTemplateHistoryDTO>;

// Choices and metadata response types
export type ChoiceItem = {
  value: string;
  label: string;
};

export type ChoicesResponseDTO = {
  purpose: ChoiceItem[];
  area: ChoiceItem[];
  status: ChoiceItem[];
  criticality: ChoiceItem[];
  owner_type: ChoiceItem[];
};

export type ChoicesResponse = {
  purpose: ChoiceItem[];
  area: ChoiceItem[];
  status: ChoiceItem[];
  criticality: ChoiceItem[];
  ownerType: ChoiceItem[];
};

export type SubareaItem = {
  value: number;
  label: string;
  name_translation_key: string;
};

export type AreaItem = {
  value: number;
  code: string;
  label: string;
  name_translation_key: string;
  display_name_translation_key: string;
  subareas: SubareaItem[];
};

export type AreasResponse = {
  areas: AreaItem[];
};

export type AssetCategoryDTO = {
  id: number;
  name: string;
};

export type AssetOwnerDTO = {
  id: number;
  email: string;
  full_name: string;
};

export type AssetItemDTO = {
  id: number;
  name: string;
  key: string;
  category: AssetCategoryDTO | null;
  owner: AssetOwnerDTO;
  importance?: string | null;
  type?: string;
};

export type AssetCategory = AssetCategoryDTO;

export type AssetOwner = {
  id: number;
  email: string;
  fullName: string;
};

export type AssetItem = {
  id: number;
  name: string;
  key: string;
  category: AssetCategory | null;
  owner: AssetOwner;
  importance: string | null;
  type?: string;
};

export type AvailableAssetsResponseDTO = {
  assets: AssetItemDTO[];
};

export type AvailableAssetsResponse = {
  assets: AssetItem[];
};

export type SubareasResponse = {
  subareas: (string | SubareaItem)[];
};

// Recommended Assets types
export type RecommendedAssetOwnerProfileDTO = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  image_url: string;
};

export type RecommendedAssetOwnerDTO = {
  id: number;
  email: string;
  last_login: string;
  is_active: boolean;
  profile: RecommendedAssetOwnerProfileDTO;
};

export type RecommendedAssetCategoryDTO = {
  id: number;
  name: string;
  name_translation_key: string;
};

export type RecommendedAssetDTO = {
  id: number;
  name: string;
  owner: RecommendedAssetOwnerDTO;
  importance: string;
  category: RecommendedAssetCategoryDTO;
  type: string;
};

export type RecommendedAssetsResponseDTO = PaginatedResponse<RecommendedAssetDTO>;

export type RecommendedAssetOwnerProfile = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
};

export type RecommendedAssetOwner = {
  id: number;
  email: string;
  lastLogin: string;
  isActive: boolean;
  profile: RecommendedAssetOwnerProfile;
};

export type RecommendedAssetCategory = {
  id: number;
  name: string;
  nameTranslationKey: string;
};

export type RecommendedAsset = {
  id: number;
  name: string;
  owner: RecommendedAssetOwner;
  importance: string;
  category: RecommendedAssetCategory;
  type: string;
};

export type RecommendedAssetsResponse = PaginatedResponse<RecommendedAsset>;

export type DeleteErrorResponse = {
  error: string;
};

export type RestoreErrorResponse = {
  error: string;
};

export type SubareasErrorResponse = {
  error: string;
};

/**
 * Request body for POST /api/v1/action-templates/subareas/ (create subarea).
 * Only name and area are sent. Slug is created automatically on the backend (not sent in request).
 */
export type SubareaCreateDTO = {
  name: string;
  area: string | number;
  is_active?: boolean;
};

/**
 * Response for POST /api/v1/action-templates/subareas/ (create subarea).
 * Aligns with schema.yaml components/schemas/SubArea. Slug is returned (generated by backend).
 */
export type SubareaResponseDTO = {
  id: number;
  name: string;
  name_translation_key: string;
  slug: string;
  is_active: boolean;
  area?: ActionTemplateAreaDataDTO;
};

// Frontend types (camelCase)
export type ActionTemplateStep = {
  id: number;
  title: string;
  shortDescription: string;
  criticality: ActionTemplateCriticality;
  criticalityDisplay: string;
  tags: Record<string, unknown>;
  reference: string;
  reportedTo: string;
  relevantFor: string;
  status: ActionTemplateStepStatus;
  statusDisplay: string;
  order: number;
  mandatory?: boolean;
};

export type ActionTemplateHistory = {
  id: number;
  actionType: ActionTemplateHistoryActionType;
  actionTypeDisplay: string;
  templateNameSnapshot: string;
  performedBy: string;
  note: string;
  createdAt: string;
};

export type ActionTemplateCreatedByDTO = {
  id: number;
  email: string;
  full_name: string;
};

export type ActionTemplateCreatedBy = {
  id: number;
  email: string;
  fullName: string;
};

export type ActionTemplate = {
  id: number;
  name: string;
  shortDescription: string;
  purpose: ActionTemplatePurpose;
  purposeDisplay: string;
  area: ActionTemplateArea;
  areaDisplay: string;
  areaData?: ActionTemplateAreaData;
  subArea: string | number | null;
  subAreaData?: ActionTemplateSubAreaData;
  status: ActionTemplateStatus;
  statusDisplay: string;
  criticality: ActionTemplateCriticality;
  criticalityDisplay: string;
  ownerType: ActionTemplateOwnerType;
  ownerTypeDisplay: string;
  actionStatus: string;
  createdAt: string;
  updatedAt: string;
  tags: Record<string, unknown>;
  steps?: ActionTemplateStep[];
  validityPeriodMonths?: number | null;
  createdBy?: ActionTemplateCreatedBy;
  ownerOrganization?: string;
  assets?: string;
  assetCategories?: AssetCategory[];
  isDeleted?: boolean;
  reference?: string;
  reportedTo?: string;
  lockedByUser?: ActionTemplateCreatedBy;
  isLocked?: boolean;
  lockInfo?: string;
  isFreshChange?: boolean;
  hasImpendingChanges?: boolean;
  lastPublishedAt?: string;
  instancesCountAllOrgs?: string;
  instancesCountOrg?: string;
};

// Filter types
export type ActionTemplateFilters = {
  purpose?: string[];
  area?: string[];
  subArea?: string[];
  status?: string[];
  criticality?: ActionTemplateCriticality[];
  creator?: ActionTemplateOwnerType[];
};

// Query params for list endpoint
export type ActionTemplateListParams = {
  limit?: number;
  offset?: number;
  search?: string;
  ordering?: string;
  area?: ActionTemplateArea;
  creator?: string;
  criticality?: ActionTemplateCriticality;
  purpose?: ActionTemplatePurpose;
  status?: ActionTemplateStatus;
  sub_area?: string;
};

// Query params for history endpoint
export type ActionTemplateHistoryParams = {
  limit?: number;
  offset?: number;
  ordering?: string;
  search?: string;
  area?: ActionTemplateArea;
  creator?: string;
  criticality?: ActionTemplateCriticality;
  purpose?: ActionTemplatePurpose;
  status?: ActionTemplateStatus;
  sub_area?: string;
};

// Query params for recommended assets endpoint
export type RecommendedAssetsParams = {
  limit?: number;
  offset?: number;
  search?: string;
};

// API interface
export interface ActionTemplateAPI {
  getAllActionTemplates: (params?: ActionTemplateListParams) => Promise<{
    data: PaginatedResponse<ActionTemplate>;
  }>;
  getSingleActionTemplate: (id: number) => Promise<{ data: ActionTemplate }>;
  createActionTemplate: (data: ActionTemplateCreateDTO) => Promise<{ data: ActionTemplate }>;
  createDraftActionTemplate: (id: number, data: ActionTemplateCreateDTO) => Promise<{ data: ActionTemplate }>;
  lockActionTemplate: (id: number) => Promise<{ data: ActionTemplate }>;
  publishActionTemplate: (id: number, data: { confirmation: boolean }) => Promise<{ data: ActionTemplate }>;
  updateActionTemplate: (id: number, data: ActionTemplateUpdateDTO) => Promise<{ data: ActionTemplate }>;
  deleteActionTemplate: (id: number) => Promise<{ data: ActionTemplate }>;
  restoreActionTemplate: (id: number) => Promise<{ data: ActionTemplate }>;
  getActionTemplateHistory: (
    id: number,
    params?: ActionTemplateHistoryParams,
  ) => Promise<{
    data: PaginatedResponse<ActionTemplateHistory>;
  }>;
  getAreas: () => Promise<{ data: AreasResponse }>;
  getAvailableAssets: () => Promise<{ data: AvailableAssetsResponse }>;
  getChoices: () => Promise<{ data: ChoicesResponse }>;
  getSubareas: (area: string) => Promise<{ data: SubareasResponse }>;
  createSubarea: (data: SubareaCreateDTO) => Promise<{ data: ActionTemplateSubAreaData }>;
  getRecommendedAssets: (
    id: number,
    params?: RecommendedAssetsParams,
  ) => Promise<{
    data: RecommendedAssetsResponse;
  }>;
}
